import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Building2, FileText, Package, Users, Trophy, FileArchive, AlertCircle, Filter, SquareUser, Sparkles, Play, Pencil, Trash2
} from 'lucide-react';
import { useEmpresa, useEmpresaDocumentos, useDocumentosLacunas, useMatchingConfig, useAtualizarMatchingConfig, useAtualizarConsultorEmpresa, useAtualizarEmpresaCadastro, useEnviarDocumentoEmpresa, useRemoverDocumentoEmpresa } from '@/hooks/useEmpresa';
import { useExecutarVarredura } from '@/hooks/useFunilVarredura';
import { useUsuarios } from '@/hooks/useUsuarios';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { KeywordChips } from '@/components/ui/KeywordChips';
import { UfMultiSelect } from '@/components/ui/UfMultiSelect';
import { DocumentosLacunas } from '@/components/empresa/DocumentosLacunas';
import { EmpresaCredenciais } from '@/components/empresa/EmpresaCredenciais';
import type { EmpresaContrato, EmpresaConcorrente, EmpresaDocumento, EmpresaProduto, EmpresaMatchingConfig, EmpresaPerfil } from '@/types/empresa';

type TabType = 'perfil' | 'funil';

function Section({ title, icon: Icon, children }: { title: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b border-ds-border bg-ds-surface2/50 pb-4">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-ds-brand" />
          <CardTitle className="text-base">{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-5">{children}</CardContent>
    </Card>
  );
}

function EmptyState({ message }: { message: string }) {
  return <p className="text-sm text-ds-text-secondary italic">{message}</p>;
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="py-1">
      <span className="text-xs font-medium text-ds-text-secondary uppercase tracking-wider">{label}</span>
      <p className="text-sm text-ds-text-primary mt-0.5">{value}</p>
    </div>
  );
}

function formatBytes(bytes: number) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
}

function formatCnpj(cnpj: string) {
  const digits = cnpj.replace(/\D/g, '');
  if (digits.length !== 14) return cnpj;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
}

function ValidadeBadge({ dataValidade }: { dataValidade: string | null }) {
  if (!dataValidade) return null;

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const validade = new Date(`${dataValidade}T00:00:00`);
  const diasRestantes = Math.round((validade.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));

  const validadeFormatada = validade.toLocaleDateString('pt-BR');

  if (diasRestantes < 0) {
    return <Badge variant="danger" size="sm">Vencido em {validadeFormatada}</Badge>;
  }
  if (diasRestantes <= 30) {
    return <Badge variant="warning" size="sm">Vence em {diasRestantes} dia{diasRestantes === 1 ? '' : 's'}</Badge>;
  }
  return <Badge variant="success" size="sm">Válido até {validadeFormatada}</Badge>;
}

function ConsultorSelector({ empresaId, consultorId }: { empresaId: string; consultorId: number | null | undefined }) {
  const { data: consultoresData, isLoading: isLoadingConsultores } = useUsuarios({ role: 'consultor', limit: 100 });
  const mutation = useAtualizarConsultorEmpresa(empresaId);

  const consultores = consultoresData?.users ?? [];

  return (
    <div className="flex items-center gap-2">
      <label htmlFor="consultor-select" className="text-xs font-medium text-ds-text-secondary whitespace-nowrap">
        Consultor responsável
      </label>
      <select
        id="consultor-select"
        value={consultorId ?? ''}
        disabled={isLoadingConsultores || mutation.isPending}
        onChange={(e) => mutation.mutate(e.target.value ? Number(e.target.value) : null)}
        className="bg-ds-surface2 border border-ds-border rounded-lg px-3 py-1.5 text-sm text-ds-text-primary focus:outline-none focus:ring-2 focus:ring-ds-brand/50 disabled:opacity-60"
      >
        <option value="">Sem consultor</option>
        {consultores.map((consultor) => (
          <option key={consultor.id} value={consultor.id}>{consultor.name}</option>
        ))}
      </select>
      {!consultorId && (
        <Badge variant="warning" size="sm">Sem consultor</Badge>
      )}
    </div>
  );
}

function FunilVisualPanel({ config }: { config: Pick<EmpresaMatchingConfig, 'palavrasChave' | 'palavrasChaveExclusao' | 'scoreMinimo' | 'ativo'> }) {
  const steps = [
    {
      title: 'Palavras-chave',
      description:
        config.palavrasChave.length > 0
          ? `${config.palavrasChave.length} termo${config.palavrasChave.length === 1 ? '' : 's'} essencial${config.palavrasChave.length === 1 ? '' : 'is'} do objeto do negócio.`
          : 'Nenhuma palavra-chave definida ainda — adicione acima para o funil filtrar por termo.',
    },
    {
      title: 'Filtragem negativa',
      description:
        config.palavrasChaveExclusao.length > 0
          ? `${config.palavrasChaveExclusao.length} palavra${config.palavrasChaveExclusao.length === 1 ? '' : 's'} de exclusão remove${config.palavrasChaveExclusao.length === 1 ? '' : 'm'} ruído e falso positivo.`
          : 'Nenhuma exclusão configurada — todo edital que casar com as palavras-chave segue adiante.',
    },
    {
      title: 'Processamento IA',
      description: 'Análise semântica classifica cada edital capturado contra o perfil da empresa.',
    },
    {
      title: 'Oportunidade real',
      description: `Match com aderência ≥ ${config.scoreMinimo}% vira alerta, pronto para decisão.`,
    },
  ];

  return (
    <Card className="overflow-hidden border-ds-brand/20">
      <CardHeader className="border-b border-ds-border bg-ds-surface2/50 pb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-ds-brand" />
          <CardTitle className="text-base">Como este funil funciona</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-5">
        {!config.ativo && (
          <div className="mb-4 rounded-lg border border-ds-warning/30 bg-ds-warning/10 p-3 text-sm text-ds-warning">
            Funil desativado — ative o interruptor acima e salve para começar a gerar alertas automáticos.
          </div>
        )}
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="space-y-4">
            {steps.map((step, index) => (
              <div key={step.title} className="flex gap-3">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ds-brand-subtle text-xs font-semibold text-ds-brand">
                  {index + 1}
                </div>
                <div>
                  <p className="text-sm font-medium text-ds-text-primary">{step.title}</p>
                  <p className="text-xs text-ds-text-secondary mt-0.5">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between rounded-lg border border-ds-border bg-ds-surface2/60 px-4 py-3">
              <span className="text-sm font-medium text-ds-text-primary">Captação bruta</span>
              <span className="text-xs text-ds-text-secondary font-mono">todos os editais</span>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-ds-border bg-ds-surface2/60 px-4 py-3">
              <span className="text-sm font-medium text-ds-text-primary">Filtragem negativa</span>
              <span className="text-xs text-ds-text-secondary font-mono">
                {config.palavrasChaveExclusao.length > 0 ? `- ${config.palavrasChaveExclusao.length} termo(s) de ruído` : 'sem exclusões'}
              </span>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-ds-border bg-ds-surface2/60 px-4 py-3">
              <span className="text-sm font-medium text-ds-text-primary">Processamento IA</span>
              <span className="text-xs text-ds-text-secondary font-mono">análise semântica</span>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-ds-brand/40 bg-ds-brand-subtle px-4 py-3">
              <span className="text-sm font-semibold text-ds-brand">Match</span>
              <span className="text-xs text-ds-brand font-mono">≥ {config.scoreMinimo}% aderência</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FunilTab({ empresaId }: { empresaId: string }) {
  const { data, isLoading } = useMatchingConfig(empresaId);
  const mutation = useAtualizarMatchingConfig(empresaId);
  const varreduraMutation = useExecutarVarredura(empresaId);
  const navigate = useNavigate();

  const [form, setForm] = useState<Pick<EmpresaMatchingConfig, 'palavrasChave' | 'palavrasChaveExclusao' | 'ufs' | 'scoreMinimo' | 'ativo'> | null>(null);
  const [activatedMessage, setActivatedMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setForm({
      palavrasChave: data.palavrasChave,
      palavrasChaveExclusao: data.palavrasChaveExclusao,
      ufs: data.ufs,
      scoreMinimo: data.scoreMinimo,
      ativo: data.ativo,
    });
  }, [data]);

  if (isLoading || !form) {
    return (
      <div className="flex items-center justify-center h-64" role="status">
        <div className="h-10 w-10 rounded-full border-2 border-ds-border border-t-ds-brand animate-spin" />
      </div>
    );
  }

  const handleSave = () => {
    const wasAtivo = data?.ativo ?? false;
    setErrorMessage(null);
    mutation.mutate(
      {
        palavras_chave: form.palavrasChave,
        palavras_chave_exclusao: form.palavrasChaveExclusao,
        ufs: form.ufs ?? [],
        score_minimo: form.scoreMinimo,
        ativo: form.ativo,
      },
      {
        onSuccess: () => {
          setActivatedMessage(form.ativo && !wasAtivo);
        },
        onError: () => {
          setErrorMessage('Não foi possível salvar a configuração do funil. Tente novamente.');
        },
      }
    );
  };

  const handleAtivarFunil = () => {
    setErrorMessage(null);
    varreduraMutation.mutate(undefined, {
      onSuccess: () => {
        navigate(`/empresas/${empresaId}/funil/varredura`);
      },
      onError: (error: Error) => {
        setErrorMessage(error.message || 'Não foi possível executar a varredura do funil.');
      },
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="border-b border-ds-border bg-ds-surface2/50 pb-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-ds-brand" />
              <CardTitle className="text-base">Funil automático (IA)</CardTitle>
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleAtivarFunil}
                isLoading={varreduraMutation.isPending}
                disabled={!data?.ativo}
                title={
                  data?.ativo
                    ? 'Executar varredura agora'
                    : 'Ative o funil e clique em "Salvar configuração" antes de executar a varredura'
                }
              >
                <Play className="h-4 w-4 mr-1.5" />
                Ativar Funil
              </Button>
              <button
                type="button"
                role="switch"
                aria-checked={form.ativo}
                aria-label="Ativar funil automático"
                onClick={() => setForm({ ...form, ativo: !form.ativo })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  form.ativo ? 'bg-ds-brand-solid' : 'bg-ds-surface2 border border-ds-border'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    form.ativo ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-5 space-y-5">
          {activatedMessage && (
            <div className="rounded-lg border border-ds-success/30 bg-ds-success/10 p-3 text-sm text-ds-success">
              Funil ativado. Os próximos alertas automáticos aparecerão aqui.
            </div>
          )}

          {form.ativo && !data?.ativo && (
            <div className="rounded-lg border border-ds-warning/30 bg-ds-warning/10 p-3 text-sm text-ds-warning">
              Clique em "Salvar configuração" para ativar o funil antes de executar a varredura.
            </div>
          )}

          {errorMessage && (
            <div className="rounded-lg border border-ds-danger/30 bg-ds-danger/10 p-3 text-sm text-ds-danger">
              {errorMessage}
            </div>
          )}

          <KeywordChips
            label="Palavras-chave de inclusão"
            value={form.palavrasChave}
            onChange={(value) => setForm({ ...form, palavrasChave: value ?? [] })}
            placeholder="Digite uma palavra-chave e pressione Enter"
          />

          <KeywordChips
            label="Palavras-chave de exclusão"
            value={form.palavrasChaveExclusao}
            onChange={(value) => setForm({ ...form, palavrasChaveExclusao: value ?? [] })}
            placeholder="Digite uma palavra a excluir e pressione Enter"
          />

          <UfMultiSelect
            value={form.ufs ?? []}
            onChange={(value) => setForm({ ...form, ufs: value })}
          />

          <Input
            type="number"
            min={0}
            max={100}
            step={1}
            label="Score mínimo para gerar alerta (0-100)"
            value={form.scoreMinimo}
            onChange={(e) => {
              const parsed = parseInt(e.target.value, 10);
              setForm({ ...form, scoreMinimo: Number.isNaN(parsed) ? 0 : Math.min(100, Math.max(0, parsed)) });
            }}
          />

          <div className="flex justify-end">
            <Button onClick={handleSave} isLoading={mutation.isPending}>
              Salvar configuração
            </Button>
          </div>
        </CardContent>
      </Card>

      <FunilVisualPanel
        config={{
          palavrasChave: form.palavrasChave,
          palavrasChaveExclusao: form.palavrasChaveExclusao,
          scoreMinimo: form.scoreMinimo,
          ativo: form.ativo,
        }}
      />
    </div>
  );
}

const CATEGORIAS_DOCUMENTO = [
  { value: 'produto', label: 'Produto' },
  { value: 'certificacao', label: 'Certificação' },
  { value: 'registro', label: 'Registro' },
  { value: 'licenca', label: 'Licença' },
  { value: 'atestado', label: 'Atestado' },
  { value: 'juridico', label: 'Jurídico' },
  { value: 'tecnico', label: 'Técnico' },
  { value: 'comercial', label: 'Comercial' },
  { value: 'estrategico', label: 'Estratégico' },
  { value: 'edital', label: 'Edital' },
  { value: 'termo_referencia', label: 'Termo de Referência' },
];

interface EmpresaEditForm {
  razao_social: string;
  cnpj: string;
  nome_fantasia: string;
  inscricao_estadual: string;
  inscricao_municipal: string;
  data_fundacao: string;
  porte: string;
  cnae_principal: string;
  cnaes_secundarios: string;
  email: string;
  telefones: string;
  site: string;
  endereco: string;
  responsavel_tecnico: string;
  responsavel_comercial: string;
  responsavel_juridico: string;
}

function buildEmpresaEditForm(empresa: EmpresaPerfil): EmpresaEditForm {
  const asString = (value?: string | null) => value ?? '';
  return {
    razao_social: empresa.razao_social ?? '',
    cnpj: empresa.cnpj ?? '',
    nome_fantasia: asString(empresa.nome_fantasia),
    inscricao_estadual: asString(empresa.inscricao_estadual),
    inscricao_municipal: asString(empresa.inscricao_municipal),
    data_fundacao: empresa.data_fundacao ? String(empresa.data_fundacao).slice(0, 10) : '',
    porte: asString(empresa.porte),
    cnae_principal: asString(empresa.cnae_principal),
    cnaes_secundarios: asString(empresa.cnaes_secundarios),
    email: asString(empresa.email),
    telefones: asString(empresa.telefones),
    site: asString(empresa.site),
    endereco: asString(empresa.endereco),
    responsavel_tecnico: asString(empresa.responsavel_tecnico),
    responsavel_comercial: asString(empresa.responsavel_comercial),
    responsavel_juridico: asString(empresa.responsavel_juridico),
  };
}

function EditarEmpresaModal({ empresa, isOpen, onClose }: { empresa: EmpresaPerfil; isOpen: boolean; onClose: () => void }) {
  const mutation = useAtualizarEmpresaCadastro();
  const [form, setForm] = useState<EmpresaEditForm>(() => buildEmpresaEditForm(empresa));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setForm(buildEmpresaEditForm(empresa));
      setErrorMessage(null);
    }
  }, [isOpen, empresa]);

  const updateField = (field: keyof EmpresaEditForm) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(null);
    try {
      await mutation.mutateAsync({ id: empresa.id, payload: { empresa: form } });
      onClose();
    } catch {
      setErrorMessage('Não foi possível salvar as alterações. Tente novamente.');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Editar empresa" contentClassName="max-w-2xl">
      <form onSubmit={handleSubmit} className="space-y-4">
        {errorMessage && (
          <div className="rounded-lg border border-ds-danger/30 bg-ds-danger/10 p-3 text-sm text-ds-danger">
            {errorMessage}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Razão Social" required value={form.razao_social} onChange={updateField('razao_social')} />
          <Input label="CNPJ" required value={form.cnpj} onChange={updateField('cnpj')} />
          <Input label="Nome Fantasia" value={form.nome_fantasia} onChange={updateField('nome_fantasia')} />
          <Input label="Inscrição Estadual" value={form.inscricao_estadual} onChange={updateField('inscricao_estadual')} />
          <Input label="Inscrição Municipal" value={form.inscricao_municipal} onChange={updateField('inscricao_municipal')} />
          <Input label="Data de Fundação" type="date" value={form.data_fundacao} onChange={updateField('data_fundacao')} />
          <Input label="Porte" value={form.porte} onChange={updateField('porte')} />
          <Input label="CNAE Principal" value={form.cnae_principal} onChange={updateField('cnae_principal')} />
          <Input label="CNAEs Secundários" value={form.cnaes_secundarios} onChange={updateField('cnaes_secundarios')} />
          <Input label="E-mail" type="email" value={form.email} onChange={updateField('email')} />
          <Input label="Telefones" value={form.telefones} onChange={updateField('telefones')} />
          <Input label="Site" value={form.site} onChange={updateField('site')} />
          <Input label="Endereço" value={form.endereco} onChange={updateField('endereco')} />
          <Input label="Responsável Técnico" value={form.responsavel_tecnico} onChange={updateField('responsavel_tecnico')} />
          <Input label="Responsável Comercial" value={form.responsavel_comercial} onChange={updateField('responsavel_comercial')} />
          <Input label="Responsável Jurídico" value={form.responsavel_juridico} onChange={updateField('responsavel_juridico')} />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button type="submit" isLoading={mutation.isPending}>
            Salvar alterações
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function DocumentoUploadForm({ empresaId }: { empresaId: string | undefined }) {
  const enviarDocumento = useEnviarDocumentoEmpresa(empresaId);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [arquivoKey, setArquivoKey] = useState(0);
  const [categoria, setCategoria] = useState(CATEGORIAS_DOCUMENTO[0].value);
  const [dataValidade, setDataValidade] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!arquivo) {
      setErrorMessage('Selecione um arquivo para enviar.');
      return;
    }
    setErrorMessage(null);
    try {
      await enviarDocumento.mutateAsync({
        arquivo,
        categoria,
        dataValidade: dataValidade || undefined,
      });
      setArquivo(null);
      setArquivoKey((current) => current + 1);
      setCategoria(CATEGORIAS_DOCUMENTO[0].value);
      setDataValidade('');
    } catch {
      setErrorMessage('Não foi possível enviar o documento. Tente novamente.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-dashed border-ds-border p-3 space-y-3">
      {errorMessage && (
        <div className="rounded-lg border border-ds-danger/30 bg-ds-danger/10 p-3 text-sm text-ds-danger">
          {errorMessage}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label htmlFor="documento-arquivo" className="block text-xs font-medium text-ds-text-secondary mb-1">
            Arquivo
          </label>
          <input
            key={arquivoKey}
            id="documento-arquivo"
            type="file"
            required
            onChange={(event) => setArquivo(event.target.files?.[0] ?? null)}
            className="w-full text-sm text-ds-text-secondary file:mr-3 file:rounded-lg file:border file:border-ds-border file:bg-ds-surface2 file:px-3 file:py-1.5 file:text-sm file:text-ds-text-primary hover:file:bg-ds-surface"
          />
        </div>
        <div>
          <label htmlFor="documento-categoria" className="block text-xs font-medium text-ds-text-secondary mb-1">
            Categoria
          </label>
          <select
            id="documento-categoria"
            value={categoria}
            onChange={(event) => setCategoria(event.target.value)}
            className="w-full bg-ds-surface2 border border-ds-border rounded-lg px-3 py-2 text-sm text-ds-text-primary focus:outline-none focus:ring-2 focus:ring-ds-brand/50"
          >
            {CATEGORIAS_DOCUMENTO.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="documento-validade" className="block text-xs font-medium text-ds-text-secondary mb-1">
            Validade (opcional)
          </label>
          <input
            id="documento-validade"
            type="date"
            value={dataValidade}
            onChange={(event) => setDataValidade(event.target.value)}
            className="w-full bg-ds-surface2 border border-ds-border rounded-lg px-3 py-2 text-sm text-ds-text-primary focus:outline-none focus:ring-2 focus:ring-ds-brand/50"
          />
        </div>
      </div>
      <div className="flex justify-end">
        <Button type="submit" size="sm" isLoading={enviarDocumento.isPending} disabled={!arquivo}>
          Enviar documento
        </Button>
      </div>
    </form>
  );
}

export function EmpresaPerfilTecnico() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('perfil');
  const [isEditOpen, setIsEditOpen] = useState(false);
  const { data: empresaData, isLoading: isLoadingEmpresa, error: empresaError } = useEmpresa(id);
  const { data: documentosData, isLoading: isLoadingDocumentos } = useEmpresaDocumentos(id);
  const { data: lacunasData, isLoading: isLoadingLacunas } = useDocumentosLacunas(id);
  const removerDocumento = useRemoverDocumentoEmpresa(id);

  if (isLoadingEmpresa) {
    return (
      <div className="flex items-center justify-center h-64" role="status">
        <div className="h-10 w-10 rounded-full border-2 border-ds-border border-t-ds-brand animate-spin" />
      </div>
    );
  }

  const apiError = empresaError as { response?: { status?: number; data?: { error?: { code?: string; message?: string } } } } | null;
  const isNotFound = apiError?.response?.status === 404 || apiError?.response?.data?.error?.code === 'NOT_FOUND';

  if (isNotFound) {
    return (
      <div className="rounded-lg border border-ds-warning/30 bg-ds-warning/10 p-8 text-center">
        <AlertCircle className="h-10 w-10 text-ds-warning mx-auto mb-3" />
        <h2 className="text-lg font-semibold text-ds-text-primary">Empresa não encontrada</h2>
        <p className="text-ds-text-secondary mt-1">Verifique o endereço ou volte para a lista de empresas.</p>
      </div>
    );
  }

  if (empresaError || !empresaData) {
    return (
      <div className="rounded-lg border border-ds-danger/30 bg-ds-danger/10 p-8 text-center text-ds-danger">
        <AlertCircle className="h-10 w-10 mx-auto mb-3" />
        <h2 className="text-lg font-semibold">Erro ao carregar perfil</h2>
        <p className="mt-1">Não foi possível carregar os dados da empresa.</p>
      </div>
    );
  }

  const empresa = empresaData;
  const documentos = documentosData ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ds-text-primary">{empresa.razao_social}</h1>
          {empresa.nome_fantasia && (
            <p className="text-ds-text-secondary">{empresa.nome_fantasia}</p>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {user?.role === 'admin' && id && (
            <ConsultorSelector empresaId={id} consultorId={empresa.consultor_id} />
          )}
          <Button type="button" variant="secondary" size="sm" onClick={() => setIsEditOpen(true)}>
            <Pencil className="h-4 w-4 mr-1.5" />
            Editar empresa
          </Button>
          <Badge variant="default" className="shrink-0">{formatCnpj(empresa.cnpj)}</Badge>
        </div>
      </div>

      <EditarEmpresaModal empresa={empresa} isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} />

      <div className="flex border-b border-ds-border">
        <button
          onClick={() => setActiveTab('perfil')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'perfil'
              ? 'border-ds-brand text-ds-brand'
              : 'border-transparent text-ds-text-secondary hover:text-ds-text-primary'
          }`}
        >
          <SquareUser className="h-4 w-4" />
          Perfil
        </button>
        <button
          onClick={() => setActiveTab('funil')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'funil'
              ? 'border-ds-brand text-ds-brand'
              : 'border-transparent text-ds-text-secondary hover:text-ds-text-primary'
          }`}
        >
          <Filter className="h-4 w-4" />
          Funil
        </button>
      </div>

      {activeTab === 'funil' && id && <FunilTab empresaId={id} />}

      {activeTab === 'perfil' && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Dados Gerais" icon={Building2}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            <InfoRow label="Razão Social" value={empresa.razao_social} />
            <InfoRow label="Nome Fantasia" value={empresa.nome_fantasia} />
            <InfoRow label="CNPJ" value={formatCnpj(empresa.cnpj)} />
            <InfoRow label="Inscrição Estadual" value={empresa.inscricao_estadual} />
            <InfoRow label="Inscrição Municipal" value={empresa.inscricao_municipal} />
            <InfoRow label="Data de Fundação" value={empresa.data_fundacao} />
            <InfoRow label="Porte" value={empresa.porte} />
            <InfoRow label="CNAE Principal" value={empresa.cnae_principal} />
            <InfoRow label="CNAEs Secundários" value={empresa.cnaes_secundarios} />
            <InfoRow label="Site" value={empresa.site} />
            <InfoRow label="E-mail" value={empresa.email} />
            <InfoRow label="Telefones" value={empresa.telefones} />
            <InfoRow label="Endereço" value={empresa.endereco} />
            <InfoRow label="Responsável Técnico" value={empresa.responsavel_tecnico} />
            <InfoRow label="Responsável Comercial" value={empresa.responsavel_comercial} />
            <InfoRow label="Responsável Jurídico" value={empresa.responsavel_juridico} />
          </div>
        </Section>

        <Section title="Atuação" icon={Trophy}>
          {!empresa.o_que_faz && !empresa.servicos_prestados && !empresa.produtos_comercializados ? (
            <EmptyState message="Nenhuma informação de atuação cadastrada." />
          ) : (
            <div className="space-y-3">
              <InfoRow label="O que faz" value={empresa.o_que_faz} />
              <InfoRow label="Serviços Prestados" value={empresa.servicos_prestados} />
              <InfoRow label="Produtos Comercializados" value={empresa.produtos_comercializados} />
              <InfoRow label="Tempo de Atuação" value={empresa.tempo_atuacao} />
              <InfoRow label="Principais Clientes" value={empresa.principais_clientes} />
              <InfoRow label="Contratos Públicos" value={empresa.contratos_publicos} />
              <InfoRow label="Contratos Privados" value={empresa.contratos_privados} />
              <InfoRow label="Estados/Municípios" value={empresa.estados_municipios} />
            </div>
          )}
        </Section>

        <Section title="Produtos" icon={Package}>
          {empresa.produtos.length === 0 ? (
            <EmptyState message="Nenhum produto cadastrado." />
          ) : (
            <div className="space-y-3">
              {empresa.produtos.map((produto: EmpresaProduto) => (
                <div key={produto.id} className="rounded-lg border border-ds-border p-3">
                  <p className="font-medium text-ds-text-primary">{produto.nome || 'Produto sem nome'}</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
                    {produto.marca && <span className="text-xs text-ds-text-secondary">Marca: {produto.marca}</span>}
                    {produto.modelo && <span className="text-xs text-ds-text-secondary">Modelo: {produto.modelo}</span>}
                    {produto.fabricante && <span className="text-xs text-ds-text-secondary">Fabricante: {produto.fabricante}</span>}
                    {produto.finalidade && <span className="text-xs text-ds-text-secondary">Finalidade: {produto.finalidade}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Contratos" icon={FileText}>
          {empresa.contratos.length === 0 ? (
            <EmptyState message="Nenhum contrato cadastrado." />
          ) : (
            <div className="space-y-3">
              {empresa.contratos.map((contrato: EmpresaContrato) => (
                <div key={contrato.id} className="rounded-lg border border-ds-border p-3">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-ds-text-primary">{contrato.cliente || 'Cliente não informado'}</p>
                    {contrato.contrato_publico && (
                      <Badge variant="info">Público</Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
                    {contrato.objeto && <span className="text-xs text-ds-text-secondary">Objeto: {contrato.objeto}</span>}
                    {contrato.valor && <span className="text-xs text-ds-text-secondary">Valor: {contrato.valor}</span>}
                    {contrato.prazo && <span className="text-xs text-ds-text-secondary">Prazo: {contrato.prazo}</span>}
                    {contrato.ano && <span className="text-xs text-ds-text-secondary">Ano: {contrato.ano}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Concorrentes" icon={Users}>
          {empresa.concorrentes.length === 0 ? (
            <EmptyState message="Nenhum concorrente cadastrado." />
          ) : (
            <div className="space-y-3">
              {empresa.concorrentes.map((concorrente: EmpresaConcorrente) => (
                <div key={concorrente.id} className="rounded-lg border border-ds-border p-3">
                  <p className="font-medium text-ds-text-primary">{concorrente.nome || 'Concorrente não informado'}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-2">
                    {concorrente.pontos_fortes && <span className="text-xs text-ds-text-secondary">Pontos fortes: {concorrente.pontos_fortes}</span>}
                    {concorrente.pontos_fracos && <span className="text-xs text-ds-text-secondary">Pontos fracos: {concorrente.pontos_fracos}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Serviços" icon={Trophy}>
          {!empresa.servicos ? (
            <EmptyState message="Nenhuma informação de serviços cadastrada." />
          ) : (
            <div className="space-y-3">
              <InfoRow label="Metodologia" value={empresa.servicos.metodologia} />
              <InfoRow label="Equipe" value={empresa.servicos.equipe} />
              <InfoRow label="Equipamentos" value={empresa.servicos.equipamentos} />
              <InfoRow label="Ferramentas" value={empresa.servicos.ferramentas} />
              <InfoRow label="Diferenciais" value={empresa.servicos.diferenciais} />
            </div>
          )}
        </Section>

        <Section title="Certificações e Registros" icon={FileArchive}>
          {!empresa.certificacoes_registros ? (
            <EmptyState message="Nenhuma certificação cadastrada." />
          ) : (
            <div className="space-y-3">
              <InfoRow label="Certificações" value={empresa.certificacoes_registros.certificacoes} />
              <InfoRow label="Registros Obrigatórios" value={empresa.certificacoes_registros.registrosObrigatorios} />
              <InfoRow label="Licenças" value={empresa.certificacoes_registros.licencas} />
            </div>
          )}
        </Section>

        <Section title="Capacidade e Estrutura" icon={Users}>
          {!empresa.capacidade_estrutura ? (
            <EmptyState message="Nenhuma informação de capacidade cadastrada." />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
              <InfoRow label="Funcionários" value={empresa.capacidade_estrutura.funcionarios} />
              <InfoRow label="Engenheiros" value={empresa.capacidade_estrutura.engenheiros} />
              <InfoRow label="Técnicos" value={empresa.capacidade_estrutura.tecnicos} />
              <InfoRow label="Operadores" value={empresa.capacidade_estrutura.operadores} />
              <InfoRow label="Administrativos" value={empresa.capacidade_estrutura.administrativos} />
              <InfoRow label="Estrutura Física" value={empresa.capacidade_estrutura.estruturaFisica} />
              <InfoRow label="Capacidade Operacional" value={empresa.capacidade_estrutura.capacidadeOperacional} />
            </div>
          )}
        </Section>

        <Section title="Diferenciais e Limitações" icon={AlertCircle}>
          {!empresa.diferenciais_limitacoes && !empresa.atestados_gerais ? (
            <EmptyState message="Nenhuma informação cadastrada." />
          ) : (
            <div className="space-y-3">
              <InfoRow label="Produtos Equivalentes" value={empresa.diferenciais_limitacoes?.produtosEquivalentes} />
              <InfoRow label="Diferenciais Competitivos" value={empresa.diferenciais_limitacoes?.diferenciaisCompetitivos} />
              <InfoRow label="Limitações" value={empresa.diferenciais_limitacoes?.limitacoes} />
              <InfoRow label="Atestados Gerais" value={empresa.atestados_gerais} />
            </div>
          )}
        </Section>

        <Section title="Histórico e Problemas com Editais" icon={AlertCircle}>
          {!empresa.historico_licitacoes && !empresa.problemas_editais ? (
            <EmptyState message="Nenhum histórico cadastrado." />
          ) : (
            <div className="space-y-3">
              <InfoRow label="Histórico de Licitações" value={empresa.historico_licitacoes} />
              <InfoRow label="Problemas Recorrentes" value={empresa.problemas_editais?.problemasRecorrentes} />
              <InfoRow label="Pontos a Combater" value={empresa.problemas_editais?.pontosACombater} />
            </div>
          )}
        </Section>

        <Section title="Documentação, Logística e Estratégico" icon={FileArchive}>
          {!empresa.documentacao && !empresa.logistica_garantias && !empresa.estrategico ? (
            <EmptyState message="Nenhuma informação cadastrada." />
          ) : (
            <div className="space-y-3">
              <InfoRow label="Documentação Jurídica" value={empresa.documentacao?.juridica} />
              <InfoRow label="Documentação Comercial" value={empresa.documentacao?.comercial} />
              <InfoRow label="Logística" value={empresa.logistica_garantias?.logistica} />
              <InfoRow label="Garantias" value={empresa.logistica_garantias?.garantias} />
              <InfoRow label="Normas Técnicas" value={empresa.logistica_garantias?.normasTecnicas} />
              <InfoRow label="Informações Estratégicas" value={empresa.estrategico?.informacoesEstrategicas} />
              <InfoRow label="Campo Final" value={empresa.estrategico?.campoFinal} />
            </div>
          )}
        </Section>

        <Section title="Documentos" icon={FileArchive}>
          <div className="space-y-4">
            <DocumentoUploadForm empresaId={id} />

            {isLoadingDocumentos ? (
              <div className="flex items-center justify-center h-20">
                <div className="h-6 w-6 rounded-full border-2 border-ds-border border-t-ds-brand animate-spin" />
              </div>
            ) : documentos.length === 0 ? (
              <EmptyState message="Nenhum documento anexado." />
            ) : (
              <div className="space-y-2">
                {documentos.map((doc: EmpresaDocumento) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between rounded-lg border border-ds-border p-3 hover:border-ds-brand/50 transition-colors"
                  >
                    <a
                      href={`${import.meta.env.VITE_API_URL?.replace('/api/v1', '')}/storage/uploads/${doc.storage_key}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between min-w-0 flex-1"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-ds-text-primary truncate">{doc.nome_arquivo}</p>
                        <p className="text-xs text-ds-text-secondary">
                          {doc.categoria} • {formatBytes(doc.tamanho_bytes)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-3">
                        <ValidadeBadge dataValidade={doc.data_validade} />
                        <FileText className="h-4 w-4 text-ds-text-secondary" />
                      </div>
                    </a>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      className="ml-3 shrink-0"
                      aria-label={`Remover ${doc.nome_arquivo}`}
                      isLoading={removerDocumento.isPending && removerDocumento.variables === doc.id}
                      onClick={() => {
                        if (window.confirm(`Remover o documento "${doc.nome_arquivo}"?`)) {
                          removerDocumento.mutate(doc.id);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {!isLoadingLacunas && lacunasData && (
              <DocumentosLacunas lacunas={lacunasData.lacunas} />
            )}
          </div>
        </Section>

        {id && (
          <div className="lg:col-span-2">
            <EmpresaCredenciais empresaId={id} />
          </div>
        )}
      </div>
      )}
    </div>
  );
}
